import { FC } from 'react'
import { EChartOption } from 'echarts'
import { EChart } from 'src/charts/EChart'
import styles from './multiDailyMentions.module.scss'
import { MultiCampaign, MultiSeries } from '../interfaces'
import { campaignColor } from '../palette'
import { wrapLabel } from '../wrapLabel'

interface MultiDailyMentionsProps {
  campaigns: MultiCampaign[]
  days: string[]
  series: MultiSeries[]
}

const COPY = {
  // Figma says "by Day of Month", which is not what the axis is: the backend
  // buckets by day offset from the window's own start, so an 85-day window
  // reads "Day 1–17", "Day 18–34". The paired report was corrected the same
  // way — the two must not name the same axis differently.
  title: 'Daily Mentions — Overlaid by Day of Period',
}

const CHART_HEIGHT = 340
const CHART_WIDTH = 1130

// The same chart the two-campaign report draws, with the series count left
// open. No area fill here: four translucent areas stacked on one plot turn the
// lower series into mud, which is the whole reason the pair version could
// afford one.
export const MultiDailyMentions: FC<MultiDailyMentionsProps> = ({
  campaigns,
  days,
  series,
}) => {
  const lines = campaigns.map((campaign, index) => ({
    color: campaignColor(index),
    name: campaign.name,
    values: series.find((row) => row.campaignId === campaign.id)?.values ?? [],
  }))

  const options: EChartOption = {
    grid: { bottom: 72, containLabel: true, left: 36, right: 36, top: 16 },
    legend: {
      bottom: 0,
      // Four full campaign names on one line overflow 1130px, so a long one is
      // wrapped inside its own legend entry.
      formatter: (name: string) => wrapLabel(name, 34),
      itemGap: 32,
      itemWidth: 24,
      textStyle: {
        color: '#25262d',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 13,
        lineHeight: 16,
      },
      type: 'plain',
    },
    series: lines.map((line) => ({
      data: line.values,
      itemStyle: { color: line.color },
      lineStyle: { color: line.color, width: 1.5 },
      name: line.name,
      smooth: false,
      symbol: 'circle',
      symbolSize: 8,
      type: 'line',
    })),
    tooltip: { axisPointer: { type: 'none' }, trigger: 'axis' },
    xAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        margin: 16,
      },
      axisLine: { lineStyle: { color: '#e6ebfa' } },
      axisTick: { show: false },
      boundaryGap: false,
      data: days,
      splitLine: {
        lineStyle: { color: '#e6ebfa', type: 'dashed' },
        show: true,
      },
      type: 'category',
    },
    yAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      min: 0,
      splitLine: { lineStyle: { color: '#e6ebfa', type: 'dashed' } },
      type: 'value',
    },
  }

  return (
    <section className={styles.card}>
      <h3 className={styles.title}>{COPY.title}</h3>

      <EChart height={CHART_HEIGHT} options={options} width={CHART_WIDTH} />
    </section>
  )
}
